import {
  collectLevelResourceReferences,
  formatAssetValidationError
} from "../../assets/manifest-validation";
import {
  AUDIO_CHANNELS,
  type AudioCatalog,
  type AudioSource
} from "../../audio";
import type { Domain, LevelDefinition } from "../../domain/contracts";

export interface OfflineAssetRequirement {
  readonly path: string;
  readonly levelIds: readonly string[];
}

export interface OfflineAssetManifestFragment {
  readonly requirements: readonly OfflineAssetRequirement[];
}

export interface VisitedIslandResourceCatalog {
  readonly validatedLevels: readonly LevelDefinition[];
  readonly audioCatalog: AudioCatalog;
  readonly assetFragments: readonly OfflineAssetManifestFragment[];
}

export interface VisitedIslandResourcePlan {
  readonly domain: Domain;
  readonly levelIds: readonly string[];
  readonly resources: readonly string[];
}

export class VisitedIslandResourcePlanningError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VisitedIslandResourcePlanningError";
  }
}

export function createVisitedIslandResourcePlan(
  domain: Domain,
  catalog: VisitedIslandResourceCatalog
): VisitedIslandResourcePlan {
  const levels = catalog.validatedLevels.filter(
    (level) => level.domain === domain
  );

  if (levels.length === 0) {
    throw new VisitedIslandResourcePlanningError(
      "No validated levels are available for " + domain
    );
  }

  const collected = collectLevelResourceReferences(levels);
  if (collected.errors.length > 0) {
    throw new VisitedIslandResourcePlanningError(
      collected.errors.map(formatAssetValidationError).join("\n")
    );
  }

  const levelIds = levels.map(({ id }) => id);
  const levelIdSet = new Set(levelIds);
  const resources = new Set(
    collected.references.map(({ path }) => path)
  );

  for (const fragment of catalog.assetFragments) {
    for (const requirement of fragment.requirements) {
      if (requirement.levelIds.some((levelId) => levelIdSet.has(levelId))) {
        resources.add(requirement.path);
      }
    }
  }

  const directlyReferencedResources = new Set(resources);

  for (const channel of AUDIO_CHANNELS) {
    for (const [trackId, track] of Object.entries(
      catalog.audioCatalog[channel]
    )) {
      if (
        directlyReferencedResources.has(trackId) ||
        track.sources.some(({ src }) => directlyReferencedResources.has(src))
      ) {
        addAudioSources(resources, track.sources);
      }
    }
  }

  const musicTheme = catalog.audioCatalog.music[domain];
  if (musicTheme === undefined) {
    throw new VisitedIslandResourcePlanningError(
      "Missing music theme for " + domain
    );
  }
  addAudioSources(resources, musicTheme.sources);

  return {
    domain,
    levelIds,
    resources: [...resources]
  };
}

function addAudioSources(
  resources: Set<string>,
  sources: readonly AudioSource[]
): void {
  for (const { src } of sources) {
    resources.add(src);
  }
}
